import { z } from 'zod';
import { cuid } from './primitives.js';

// ---------- Categorias (arvore por empresa; subcategoria opcional) ----------

export const criarCategoriaSchema = z.object({ nome: z.string().trim().min(1, 'Informe o nome da categoria').max(80, 'O nome tem no máximo 80 caracteres'), paiId: cuid.nullish() });
export type CriarCategoriaDto = z.infer<typeof criarCategoriaSchema>;

/** PATCH /categorias/:id: renomear e/ou mover (paiId null = para a raiz). */
export const atualizarCategoriaSchema = z
  .object({ nome: z.string().trim().min(1).max(80), paiId: cuid.nullable() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarCategoriaDto = z.infer<typeof atualizarCategoriaSchema>;

/** POST /categorias/:id/mover-itens: leva os itens da categoria para outra (ou para "sem categoria"). */
export const moverItensCategoriaSchema = z.object({ paraId: cuid.nullable() });
export type MoverItensCategoriaDto = z.infer<typeof moverItensCategoriaSchema>;

/** POST /categorias/mesclar (Sessao 37): leva itens e subcategorias das categorias de origem para a de destino e apaga as de origem. */
export const mesclarCategoriasSchema = z.object({
  destinoId: cuid,
  origemIds: z.array(cuid).min(1, 'Escolha ao menos uma categoria de origem').max(100, 'No maximo 100 categorias por vez'),
});
export type MesclarCategoriasDto = z.infer<typeof mesclarCategoriasSchema>;
export interface MesclarCategoriasResponse {
  itensMovidos: number;
  subcategoriasMovidas: number;
  categoriasApagadas: number;
}

/** GET /categorias/duplicadas: grupos de categorias com o mesmo nome normalizado sob o mesmo pai. `destinoSugeridoId` = a de nome mais curto (o original). */
export interface CategoriaDuplicadaGrupo {
  chave: string;
  destinoSugeridoId: string;
  categorias: CategoriaResponse[];
}
export interface CategoriasDuplicadasResponse {
  grupos: CategoriaDuplicadaGrupo[];
}

export interface CategoriaResponse {
  id: string;
  nome: string;
  paiId: string | null;
  /** Caminho legivel ("Embalagens > Caixas"). */
  caminho: string;
  nivel: number;
  /** Itens diretamente nesta categoria. */
  itens: number;
  filhas: number;
}
export interface CategoriasResponse {
  categorias: CategoriaResponse[];
  /** Itens da empresa sem categoria. */
  semCategoria: number;
}

// ---------- Unidades e conversoes ----------

export const TIPOS_UNIDADE = ['MASSA', 'VOLUME', 'COMPRIMENTO', 'AREA', 'UNIDADE', 'TEMPO'] as const;
export const tipoUnidade = z.enum(TIPOS_UNIDADE);
export type TipoUnidade = z.infer<typeof tipoUnidade>;
export const ROTULO_TIPO_UNIDADE: Record<TipoUnidade, string> = { MASSA: 'Massa', VOLUME: 'Volume', COMPRIMENTO: 'Comprimento', AREA: 'Área', UNIDADE: 'Unidade', TEMPO: 'Tempo' };

export const criarUnidadeSchema = z.object({
  codigo: z.string().trim().min(1, 'Informe o código').max(10, 'O código tem no máximo 10 caracteres'),
  descricao: z.string().trim().min(1, 'Informe a descrição').max(60, 'A descrição tem no máximo 60 caracteres'),
  tipo: tipoUnidade,
});
export type CriarUnidadeDto = z.infer<typeof criarUnidadeSchema>;
export const atualizarUnidadeSchema = criarUnidadeSchema.partial().refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarUnidadeDto = z.infer<typeof atualizarUnidadeSchema>;

/** 1 `deId` = `fator` `paraId` (t -> kg = 1000). Mesmo tipo de unidade nas duas pontas; salvar de novo o mesmo par troca o fator. */
export const salvarConversaoSchema = z.object({
  deId: cuid,
  paraId: cuid,
  fator: z.string().regex(/^\d{1,9}(\.\d{1,6})?$/, 'Fator inválido (decimal até 6 casas)').refine((v) => Number(v) > 0, 'O fator deve ser maior que zero'),
});
export type SalvarConversaoDto = z.infer<typeof salvarConversaoSchema>;

export interface ConversaoUnidadeResponse {
  id: string;
  deId: string;
  deCodigo: string;
  paraId: string;
  paraCodigo: string;
  fator: string;
}
export interface UnidadeResponse {
  id: string;
  codigo: string;
  descricao: string;
  tipo: TipoUnidade;
  /** Itens que usam a unidade (venda ou custo). */
  itens: number;
  conversoes: ConversaoUnidadeResponse[];
  /** Vem do Protheus (rotina em IMPORTAR): os campos de `CAMPOS_DO_PROTHEUS` sao somente leitura; a API recusa a edicao (409 CAMPO_DO_PROTHEUS). */
  doProtheus?: boolean;
}
export interface UnidadesResponse {
  unidades: UnidadeResponse[];
}

/** GET /unidades/fator?de=t&para=kg (codigos, sem diferenciar caixa): o fator "de -> para" do cadastro, direto ou pelo inverso. */
export const fatorUnidadeQuerySchema = z.object({ de: z.string().trim().min(1).max(10), para: z.string().trim().min(1).max(10) });
export type FatorUnidadeQuery = z.infer<typeof fatorUnidadeQuerySchema>;
export interface FatorUnidadeResponse {
  de: string;
  para: string;
  /** null = sem conversao cadastrada entre as duas. */
  fator: string | null;
  /** true = calculado como 1 / fator do cadastro "para -> de". */
  inverso: boolean;
}
